import { InputHTMLAttributes } from 'react'
import {
  DisplayProps,
  LayoutProps,
  MarginProps,
  PositionProps,
} from 'styled-system'

import { TestIdProps } from 'designSystem/types/propTypes'

import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'

import PDSText from '../PDSText/PDSText'
import INTERNALxDiv from '../internal/elements/INTERNALxDiv'
import INTERNALxInput from '../internal/elements/INTERNALxInput'
import INTERNALxLabel from '../internal/elements/INTERNALxLabel'

type SwitchSize = 'sm' | 'md' | 'lg'
type SwitchVariant = 'normal' | 'purple'

type BaseProps = InputHTMLAttributes<HTMLInputElement> &
  DisplayProps &
  MarginProps &
  LayoutProps &
  PositionProps &
  TestIdProps

type Props = Omit<BaseProps, 'size'> & {
  label?: string
  offText?: string
  onText?: string
  size?: SwitchSize
  variant?: SwitchVariant
}

export default function PDSSwitch({
  checked,
  defaultChecked,
  disabled,
  label,
  offText,
  onText,
  size = 'md',
  testId,
  variant = 'normal',
  ...props
}: Props) {
  const state = checked ? 'on' : 'off'

  const disabledClass = disabled ? 'disabled' : ''
  const sizeStyles = PDS_SWITCH_SIZE[size] ?? PDS_SWITCH_SIZE.sm

  return (
    <INTERNALxLabel
      className={`switch ${disabledClass} ${variant}`}
      display="inline-block"
      testId={testId}
    >
      <INTERNALxDiv paddingBottom={label ? SIZE.s8 : SIZE.s0}>
        <PDSText fontSize={sizeStyles.text.fontSize} weight="medium">
          {label}
        </PDSText>
      </INTERNALxDiv>
      <INTERNALxDiv alignItems="center" display="flex" gap={SIZE.s4}>
        <INTERNALxInput
          checked={checked}
          className="switch-input"
          defaultChecked={defaultChecked}
          disabled={disabled}
          height={SIZE.s0}
          style={{ visibility: 'hidden' }}
          position="absolute"
          testId={testId && `${testId}-input`}
          type="checkbox"
          width={SIZE.s0}
          {...props}
        />

        <INTERNALxDiv
          borderRadius={SIZE.s72}
          className={`switch-body ${state}`}
          overflow="hidden"
          position="relative"
          {...sizeStyles.body}
        >
          <INTERNALxDiv
            backgroundColor={COLOR.white}
            borderRadius="100%"
            className={`switch-handle ${state} ${size}`}
            position="absolute"
            {...sizeStyles.handle}
          />
        </INTERNALxDiv>

        {(onText || offText) && (
          <>
            <PDSText
              className="switch-text-on"
              fontSize={sizeStyles.text.fontSize}
              weight="medium"
            >
              {onText}
            </PDSText>
            <PDSText
              className="switch-text-off"
              fontSize={sizeStyles.text.fontSize}
              weight="medium"
            >
              {offText}
            </PDSText>
          </>
        )}
      </INTERNALxDiv>
    </INTERNALxLabel>
  )
}

const PDS_SWITCH_SIZE = {
  sm: {
    body: {
      borderWidth: '1.5px',
      height: '1.25rem',
      marginTop: SIZE.s0,
      width: '2.25rem',
    },
    handle: {
      height: '1.0675rem',
      width: '1.0675rem',
    },
    text: {
      fontSize: FONT_SIZE.xxs,
    },
  },
  md: {
    body: {
      borderWidth: '2px',
      height: '1.5rem',
      marginTop: SIZE.s2,
      width: '2.5rem',
    },
    handle: {
      height: '1.25rem',
      width: '1.25rem',
    },
    text: {
      fontSize: FONT_SIZE.xs,
    },
  },
  lg: {
    body: {
      borderWidth: '2px',
      height: '1.75rem',
      marginTop: SIZE.s4,
      width: '3rem',
    },
    handle: {
      height: '1.5rem',
      width: '1.5rem',
    },
    text: {
      fontSize: FONT_SIZE.sm,
    },
  },
}
